import { useEffect, useRef } from "react";
import { getRetentionConfig, runRetentionCleanupIfNeeded, touchLastActivity } from "@/utils/dataRetention";

export const useDataRetention = (opts?: { onPurged?: () => void }) => {
  const lastTouchRef = useRef(0);
  const purgeLockRef = useRef(false);

  useEffect(() => {
    const cfg = getRetentionConfig();

    const safePurgeCheck = async (now: number) => {
      if (purgeLockRef.current) return;
      purgeLockRef.current = true;
      try {
        const result = await runRetentionCleanupIfNeeded(now);
        if (result.purged) opts?.onPurged?.();
      } finally {
        purgeLockRef.current = false;
      }
    };

    const throttledTouch = () => {
      const now = Date.now();
      if (now - lastTouchRef.current < cfg.activityThrottleMs) return;
      lastTouchRef.current = now;
      touchLastActivity(now);
    };

    void safePurgeCheck(Date.now());

    const events: Array<[keyof WindowEventMap, AddEventListenerOptions | boolean | undefined]> = [
      ["pointerdown", { passive: true }],
      ["keydown", undefined],
      ["scroll", { passive: true }],
      ["focus", undefined],
    ];

    const onActivity = () => throttledTouch();
    const onVisibility = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "visible") {
        throttledTouch();
        void safePurgeCheck(Date.now());
      }
    };

    for (const [evt, options] of events) {
      window.addEventListener(evt, onActivity as EventListener, options as any);
    }
    document.addEventListener("visibilitychange", onVisibility);

    const timer = window.setInterval(() => {
      void safePurgeCheck(Date.now());
    }, cfg.checkIntervalMs);

    return () => {
      window.clearInterval(timer);
      for (const [evt, options] of events) {
        window.removeEventListener(evt, onActivity as EventListener, options as any);
      }
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [opts]);
};
